// Gemeente audit-log met filter-pills + uitklapbare motivation. PRD-05 §3.

import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { api, ApiError } from "@/lib/api";
import { MunicipalityLayout } from "@/components/MunicipalityLayout";

interface AuditEntry {
  id: string;
  action: string;
  objectType: string | null;
  objectId: string | null;
  motivation: string | null;
  outcome: string;
  createdAt: string;
  userId: string | null;
  userRole: string | null;
}

const ACTION_STYLE: Record<string, string> = {
  "municipality.dossier_access": "bg-purple-100 text-purple-900",
  "counselor.client_view": "bg-blue-100 text-blue-900",
  "ember.concept.approve": "bg-green-100 text-green-900",
  "ember.concept.reject": "bg-red-100 text-red-900",
  "auth.login": "bg-neutral-100 text-neutral-700",
};

type FilterKey = "all" | "access" | "concepts" | "logins";

export function MunicipalityAuditPage() {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<FilterKey>("all");
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const query = useQuery({
    queryKey: ["municipality-audit"],
    queryFn: () => api.get<{ entries: AuditEntry[] }>("/v1/municipality/audit-log"),
    staleTime: 30_000,
    refetchInterval: 30_000,
  });

  const errorKey =
    query.error instanceof ApiError ? query.error.i18nKey : "errors.internal";

  const all = query.data?.entries ?? [];

  const entries = useMemo(() => {
    if (filter === "all") return all;
    if (filter === "access")
      return all.filter((e) => e.action === "municipality.dossier_access");
    if (filter === "concepts")
      return all.filter(
        (e) => e.action === "ember.concept.approve" || e.action === "ember.concept.reject"
      );
    if (filter === "logins") return all.filter((e) => e.action === "auth.login");
    return all;
  }, [all, filter]);

  function toggle(id: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const filters: { key: FilterKey; labelKey: string; default: string }[] = [
    { key: "all", labelKey: "municipality.audit.filter.all", default: "Alle" },
    { key: "access", labelKey: "municipality.audit.filter.access", default: "Dossier-access" },
    {
      key: "concepts",
      labelKey: "municipality.audit.filter.concepts",
      default: "Concept-actions",
    },
    { key: "logins", labelKey: "municipality.audit.filter.logins", default: "Logins" },
  ];

  return (
    <MunicipalityLayout title={t("municipality.audit.title", "Audit-log")}>
      <div className="flex flex-wrap gap-2 mb-4">
        {filters.map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${
              filter === f.key
                ? "bg-brand-50 border-brand-300 text-brand-800 font-medium"
                : "bg-white border-neutral-200 text-neutral-700 hover:bg-neutral-50"
            }`}
          >
            {t(f.labelKey, f.default)}
          </button>
        ))}
      </div>

      <div className="bg-white border border-neutral-200 rounded-xl overflow-hidden">
        {query.isLoading ? (
          <p className="p-5 text-sm text-neutral-500">{t("loading", "Laden…")}</p>
        ) : query.isError ? (
          <p className="p-5 text-sm text-red-700">{t(errorKey, "Onverwachte fout")}</p>
        ) : entries.length === 0 ? (
          <p className="p-5 text-sm text-neutral-500">
            {t("municipality.audit.empty", "Geen audit-events gevonden.")}
          </p>
        ) : (
          <ul className="divide-y divide-neutral-100">
            {entries.map((e) => {
              const isAccess = e.action === "municipality.dossier_access";
              const isExpanded = expanded.has(e.id);
              const badgeClass = ACTION_STYLE[e.action] ?? "bg-neutral-100 text-neutral-700";
              return (
                <li key={e.id} className="px-5 py-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className={`text-xs px-2 py-0.5 rounded ${badgeClass}`}>
                          {e.action}
                        </span>
                        {e.userRole && (
                          <span className="text-xs px-2 py-0.5 rounded bg-neutral-100 text-neutral-600">
                            {e.userRole}
                          </span>
                        )}
                        {e.outcome !== "SUCCESS" && (
                          <span className="text-xs px-2 py-0.5 rounded bg-red-100 text-red-900">
                            {e.outcome}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-neutral-600">
                        {e.objectType ?? "—"}
                        {e.objectId ? (
                          <span className="font-mono text-[10px] text-neutral-400">
                            {" "}
                            · {e.objectId.slice(0, 8)}
                          </span>
                        ) : null}
                      </p>
                      {isAccess && e.motivation && (
                        <button
                          onClick={() => toggle(e.id)}
                          className="mt-1 text-xs text-brand-700 hover:text-brand-900"
                        >
                          {isExpanded
                            ? "− " + t("municipality.audit.col.motivation", "Motivatie")
                            : "+ " + t("municipality.audit.col.motivation", "Motivatie")}
                        </button>
                      )}
                      {isAccess && e.motivation && isExpanded && (
                        <p className="mt-2 text-xs bg-neutral-50 border border-neutral-200 rounded-lg p-2 text-neutral-700 whitespace-pre-wrap">
                          {e.motivation}
                        </p>
                      )}
                    </div>
                    <time className="text-xs text-neutral-400 whitespace-nowrap">
                      {new Date(e.createdAt).toLocaleString("nl-NL", {
                        dateStyle: "short",
                        timeStyle: "short",
                      })}
                    </time>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </MunicipalityLayout>
  );
}
